import React from 'react';
import ReactDOM from 'react-dom/client';

import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';

import App from './App';
import { msalConfig, isAuthEnabled, setMsalInstance } from './auth';
import theme from './theme/theme';

dayjs.locale('en-gb');

// Validate redirect URL to prevent open redirect attacks
const isValidRedirectUrl = (url: string): boolean => {
  // Only allow relative URLs or same-origin URLs
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return parsedUrl.origin === window.location.origin;
  } catch {
    return false;
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const renderApp = async () => {
  const root = ReactDOM.createRoot(document.getElementById('root')!);

  let msalInstance: PublicClientApplication | null = null;

  if (isAuthEnabled()) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    setMsalInstance(msalInstance);

    // Register callback BEFORE handleRedirectPromise so we catch the LOGIN_SUCCESS event
    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        msalInstance?.setActiveAccount(payload.account);

        // Redirect to saved URL after login (with validation to prevent open redirect)
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          if (isValidRedirectUrl(redirectUrl)) {
            window.location.href = redirectUrl;
          } else {
            console.warn('Invalid redirect URL blocked:', redirectUrl);
            window.location.href = '/';
          }
        }
      }
    });

    await msalInstance.handleRedirectPromise();

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  const AppContent = (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );

  root.render(
    <React.StrictMode>
      {msalInstance ? (
        <MsalProvider instance={msalInstance}>{AppContent}</MsalProvider>
      ) : (
        AppContent
      )}
    </React.StrictMode>,
  );
};

renderApp().catch(console.error);
